import { useState, useEffect } from 'react'

const useInstagramFeed = (username, count = 6, options = {}) => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const postUrls = Array.isArray(options?.postUrls) ? options.postUrls.filter(Boolean) : []
    const latestEndpoint = typeof options?.latestEndpoint === 'string' ? options.latestEndpoint : ''
    const localFallbackPosts = Array.isArray(options?.fallbackPosts) ? options.fallbackPosts.filter(Boolean) : []

    const fetchInstagramFeed = async () => {
      try {
        setLoading(true)
        setError(null)
        setPosts([])

        if (latestEndpoint) {
          const latestPosts = await fetchLatestPosts(latestEndpoint)
          if (latestPosts.length > 0) {
            setPosts(latestPosts.slice(0, count))
            return
          }
        }

        if (postUrls.length > 0) {
          // Obtener miniaturas a partir de las meta etiquetas OG de cada post
          const fetched = await Promise.allSettled(
            postUrls.slice(0, count).map(async (permalink, index) => {
              const proxiedUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(permalink)}`
              const res = await fetch(proxiedUrl)
              if (!res.ok) throw new Error('Failed to fetch post HTML')
              const html = await res.text()
              const ogImage = extractOgImageFromHtml(html)
              const caption = extractOgTitleFromHtml(html)
              const post = {
                id: `post_${index}`,
                thumbnail: ogImage,
                display_url: ogImage,
                caption: caption || '',
                permalink,
                position: index,
                timestamp: Date.now() - index * 1000,
                likes: 0
              }
              publishPost(post)
              return post
            })
          )

          const successful = fetched
            .filter(r => r.status === 'fulfilled' && r.value && r.value.thumbnail)
            .map(r => r.value)

          if (successful.length === 0) {
            throw new Error('No thumbnails extracted from provided post URLs')
          }

          return
        }

        // Modo legacy: intentar obtener desde el perfil público (menos confiable)
        const instagramUrl = `https://www.instagram.com/${username}/`
        const response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(instagramUrl)}`)
        if (!response.ok) {
          throw new Error('Failed to fetch Instagram profile')
        }

        const html = await response.text()
        const scriptMatch = html.match(/window\._sharedData\s*=\s*({.+?});/)
        if (!scriptMatch) {
          throw new Error('No Instagram data found in response')
        }

        const instagramData = JSON.parse(scriptMatch[1])
        const user = instagramData?.entry_data?.ProfilePage?.[0]?.graphql?.user
        if (!user) {
          throw new Error('User data not found')
        }

        const edges = user.edge_owner_to_timeline_media?.edges || []
        const formattedPosts = edges.slice(0, count).map((edge, index) => ({
          id: edge.node.id,
          thumbnail: edge.node.thumbnail_src || edge.node.display_url,
          display_url: edge.node.display_url,
          caption: edge.node.edge_media_to_caption?.edges[0]?.node?.text || '',
          permalink: `https://www.instagram.com/p/${edge.node.shortcode}/`,
          position: index,
          timestamp: edge.node.taken_at_timestamp,
          likes: edge.node.edge_liked_by?.count || 0
        }))

        if (formattedPosts.length === 0) {
          throw new Error('No posts found')
        }

        setPosts(formattedPosts)
      } catch (err) {
        console.warn('Primary Instagram method failed, trying alternative:', err)
        
        // Método alternativo: usar InstagramBasicDisplayApi proxy
        try {
          await fetchWithRapidAPI()
        } catch (err2) {
          console.warn('All Instagram methods failed, using fallback:', err2)
          createFallbackPosts()
        }
      } finally {
        setLoading(false)
      }
    }

    const fetchWithRapidAPI = async () => {
      // Método alternativo usando un servicio de terceros
      // Nota: Esto requeriría una API key, pero como es para demo, fallback a placeholder
      throw new Error('RapidAPI method not configured')
    }

    const createFallbackPosts = () => {
      const fallbackPosts = Array.from({ length: count }, (_, index) => ({
        ...(localFallbackPosts[index % localFallbackPosts.length] || {}),
        id: `fallback_${index}`,
        position: index,
        timestamp: Date.now() - index * 86400000,
        likes: 0
      }))
      
      setPosts(fallbackPosts)
      setError(null) // No mostrar como error, sino como contenido demo
    }

    const publishPost = (post) => {
      setPosts(currentPosts => {
        const nextPosts = currentPosts.filter(currentPost => currentPost.position !== post.position)
        return [...nextPosts, post].sort((firstPost, secondPost) => firstPost.position - secondPost.position)
      })
    }

    const fetchLatestPosts = async (endpoint) => {
      try {
        const response = await fetch(endpoint)
        if (!response.ok) return []

        const payload = await response.json()
        const rawPosts = Array.isArray(payload?.posts) ? payload.posts : []

        return rawPosts
          .map((post, index) => {
            const imageUrl = post.thumbnail || post.thumbnail_url || post.media_url || post.display_url
            if (!imageUrl) return null

            return {
              id: post.id || `latest_${index}`,
              thumbnail: imageUrl,
              display_url: post.display_url || imageUrl,
              caption: post.caption || '',
              permalink: post.permalink || `https://www.instagram.com/${username}/`,
              position: index,
              timestamp: post.timestamp || Date.now() - index * 1000,
              likes: 0
            }
          })
          .filter(Boolean)
      } catch (err) {
        console.warn('Latest Instagram endpoint failed, using curated posts:', err)
        return []
      }
    }

    const extractOgImageFromHtml = (html) => {
      const match = html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["'][^>]*>/i)
      if (match && match[1]) return decodeHTMLEntities(match[1])
      const matchTwitter = html.match(/<meta[^>]+name=["']twitter:image["'][^>]+content=["']([^"']+)["'][^>]*>/i)
      if (matchTwitter && matchTwitter[1]) return decodeHTMLEntities(matchTwitter[1])
      return null
    }

    const extractOgTitleFromHtml = (html) => {
      const match = html.match(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)["'][^>]*>/i)
      if (match && match[1]) return decodeHTMLEntities(match[1])
      return ''
    }

    const decodeHTMLEntities = (text) => {
      if (!text) return text
      return text
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
    }

    fetchInstagramFeed()
  }, [username, count, JSON.stringify(options?.postUrls || []), options?.latestEndpoint, JSON.stringify(options?.fallbackPosts || [])])

  return { posts, loading, error }
}

export default useInstagramFeed
